/**
 * Service-level Insights tab.
 *
 * Operator-authored dashboards backed by the restored
 * ``/api/v1/dashboards`` + ``/api/v1/promql/query_range`` endpoints.
 * The tab is intentionally minimal:
 *
 *   - one dashboard active at a time (dropdown picker)
 *   - per-panel recharts viewer with a shared time-range selector
 *   - inline editor opened via a Modal so day-to-day viewing stays
 *     uncluttered
 *
 * PromQL uses ``$service`` as a placeholder; the controller resolves
 * it to the service slug server-side (see
 * ``controller/api/routers/promql.py``), so a forged ``service_id``
 * can't widen the panel's scope.
 */
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChevronDown,
  LayoutDashboard,
  LineChart as LineChartIcon,
  Pencil,
  Plus,
  RefreshCcw,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";

import {
  defaultServicePanels,
  useCreateDashboard,
  useDashboards,
  useDeleteDashboard,
  usePanelRange,
  useUpdateDashboard,
  PANEL_UNITS,
  RANGE_OPTIONS,
  type CustomDashboard,
  type DashboardScope,
  type Panel,
  type PanelSeries,
  type PanelUnit,
  type PromQLSeries,
  type RangeOptionId,
} from "@/api/local/dashboards";
import { useConfirm } from "@/components/ConfirmDialog";
import { Modal } from "@/components/Modal";
import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";
import type { Service } from "@/api/types";

const SERIES_COLORS = [
  "#facc15",
  "#38bdf8",
  "#fb7185",
  "#a78bfa",
  "#34d399",
  "#fb923c",
  "#f472b6",
  "#60a5fa",
];

interface Props {
  service: Service;
}

export function ServiceInsights({ service }: Props) {
  const scope: DashboardScope = useMemo(
    () => ({ kind: "service", service_id: service.id }),
    [service.id],
  );
  const list = useDashboards(scope);
  const create = useCreateDashboard(scope);
  const update = useUpdateDashboard(scope);
  const remove = useDeleteDashboard(scope);
  const confirm = useConfirm();

  const dashboards = list.data ?? [];
  const [activeId, setActiveId] = useState<number | null>(null);
  const [rangeId, setRangeId] = useState<RangeOptionId>("1h");
  const [editorOpen, setEditorOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Default-select the first dashboard once the list resolves and
  // re-pick when the active one is deleted out from under us.
  useEffect(() => {
    if (dashboards.length === 0) {
      setActiveId(null);
      return;
    }
    if (activeId == null || !dashboards.some((d) => d.id === activeId)) {
      setActiveId(dashboards[0].id);
    }
  }, [dashboards, activeId]);

  const active = useMemo(
    () => dashboards.find((d) => d.id === activeId) ?? null,
    [dashboards, activeId],
  );
  const range = RANGE_OPTIONS.find((r) => r.id === rangeId) ?? RANGE_OPTIONS[1];

  const seedStarter = async () => {
    setCreating(true);
    try {
      const created = await create({
        name: "Overview",
        scope,
        panels: defaultServicePanels(),
      });
      setActiveId(created.id);
    } finally {
      setCreating(false);
    }
  };

  const createBlank = async () => {
    setCreating(true);
    try {
      const created = await create({
        name: `Dashboard ${dashboards.length + 1}`,
        scope,
        panels: [blankPanel(0)],
      });
      setActiveId(created.id);
      setEditorOpen(true);
    } finally {
      setCreating(false);
    }
  };

  if (list.isLoading) {
    return (
      <div className="muted" style={{ padding: 24 }}>
        loading dashboards…
      </div>
    );
  }

  if (dashboards.length === 0) {
    return (
      <Section
        title="Insights"
        icon={<LineChartIcon />}
        count={<Pill tone="neutral">empty</Pill>}
      >
        <div
          style={{
            display: "grid",
            placeItems: "center",
            gap: 16,
            padding: "32px 16px",
          }}
        >
          <LayoutDashboard size={36} className="muted" />
          <div style={{ textAlign: "center", maxWidth: 520 }}>
            <h4 style={{ margin: "0 0 4px" }}>No dashboards yet</h4>
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>
              Insights panels run operator-authored PromQL against the
              cluster's Prometheus through the controller's authenticated
              proxy. Start from a generic Envoy-shaped overview or build
              one from scratch.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn btn--primary"
              onClick={seedStarter}
              disabled={creating}
            >
              <Sparkles />
              create starter dashboard
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={createBlank}
              disabled={creating}
            >
              <Plus />
              blank dashboard
            </button>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <>
      <div
        className="toolbar"
        style={{
          marginBottom: 12,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div style={{ position: "relative" }}>
          <select
            value={activeId ?? ""}
            onChange={(e) => setActiveId(Number(e.target.value))}
            style={{ paddingRight: 28 }}
          >
            {dashboards.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              opacity: 0.6,
            }}
          />
        </div>

        <div role="group" style={{ display: "flex", gap: 4 }}>
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`btn btn--sm ${
                r.id === rangeId ? "btn--primary" : "btn--ghost"
              }`}
              onClick={() => setRangeId(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>

        <span style={{ flex: 1 }} />

        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setEditorOpen(true)}
          disabled={!active}
        >
          <Pencil />
          edit
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={createBlank}
          disabled={creating}
        >
          <Plus />
          new
        </button>
        <button
          type="button"
          className="btn btn--danger btn--sm"
          disabled={!active}
          onClick={async () => {
            if (!active) return;
            const ok = await confirm({
              title: `Delete dashboard "${active.name}"?`,
              tone: "danger",
              confirmLabel: "delete",
            });
            if (!ok) return;
            await remove(active.id);
          }}
        >
          <Trash2 />
          delete
        </button>
      </div>

      {active && active.panels.length === 0 && (
        <div className="flash" style={{ marginBottom: 12 }}>
          This dashboard has no panels. Press <strong>edit</strong> to add
          one.
        </div>
      )}

      <div className="dashboard-grid">
        {(active?.panels ?? []).map((panel) => (
          <PanelView
            key={panel.id}
            panel={panel}
            scope={scope}
            windowSeconds={range.windowSeconds}
            stepSeconds={range.stepSeconds}
          />
        ))}
      </div>

      {editorOpen && active && (
        <DashboardEditor
          dashboard={active}
          onClose={() => setEditorOpen(false)}
          onSave={async (patch) => {
            await update(active.id, patch);
            setEditorOpen(false);
          }}
        />
      )}
    </>
  );
}

// ---- panel viewer -------------------------------------------------------

interface PanelViewProps {
  panel: Panel;
  scope: DashboardScope;
  windowSeconds: number;
  stepSeconds: number;
}

function PanelView({ panel, scope, windowSeconds, stepSeconds }: PanelViewProps) {
  const q = usePanelRange({
    panelId: `s${scope.service_id}-${panel.id}`,
    series: panel.series,
    scope,
    windowSeconds,
    stepSeconds,
  });

  const data = useMemo(
    () => toRechartsRows(q.data?.series ?? [], panel.series),
    [q.data, panel.series],
  );

  // Grid: panels use ``w`` in 12-column units; map to a flex-basis %.
  const widthPct = `${(panel.w / 12) * 100}%`;

  return (
    <div
      className="section"
      style={{
        flex: `1 1 calc(${widthPct} - 12px)`,
        minWidth: 320,
        boxSizing: "border-box",
      }}
    >
      <div className="section__header">
        <h3 className="section__title">
          <LineChartIcon />
          <span className="section__title-text">{panel.title}</span>
          {q.isFetching && (
            <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
              <RefreshCcw size={11} /> refreshing…
            </span>
          )}
        </h3>
        <div className="section__actions">
          <span className="muted" style={{ fontSize: 11 }}>
            {panel.unit}
          </span>
        </div>
      </div>
      <div className="section__body">
        {q.isError && (
          <div className="flash">
            <strong>query failed:</strong>{" "}
            <span className="mono">{(q.error as Error).message}</span>
          </div>
        )}
        {!q.isError && data.length === 0 && !q.isLoading && (
          <div className="muted" style={{ padding: 12, fontSize: 12 }}>
            no data in window
          </div>
        )}
        {data.length > 0 && (
          <ResponsiveContainer width="100%" height={panel.h}>
            <LineChart data={data}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="ts"
                tick={{ fontSize: 10 }}
                tickFormatter={(v) => formatTs(v as number)}
                stroke="rgba(255,255,255,0.4)"
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={(v) => formatValue(v as number, panel.unit)}
                stroke="rgba(255,255,255,0.4)"
                width={56}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--edge)",
                  fontSize: 12,
                }}
                labelFormatter={(v) =>
                  new Date(v as number).toLocaleTimeString()
                }
                formatter={(v: number) => formatValue(v, panel.unit)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {panel.series.map((s, idx) => (
                <Line
                  key={`${idx}-${s.legend}`}
                  type="monotone"
                  dataKey={`s${idx}`}
                  name={s.legend}
                  stroke={s.color || SERIES_COLORS[idx % SERIES_COLORS.length]}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                  strokeWidth={1.5}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ---- dashboard editor (Modal) ------------------------------------------

interface EditorProps {
  dashboard: CustomDashboard;
  onClose: () => void;
  onSave: (patch: { name?: string; panels?: Panel[] }) => Promise<void>;
}

function DashboardEditor({ dashboard, onClose, onSave }: EditorProps) {
  const [name, setName] = useState(dashboard.name);
  const [panels, setPanels] = useState<Panel[]>(dashboard.panels);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dirty =
    name !== dashboard.name ||
    JSON.stringify(panels) !== JSON.stringify(dashboard.panels);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const stripped = panels.map((p) => ({
        ...p,
        series: p.series.filter((s) => s.promql.trim() && s.legend.trim()),
      }));
      const empty = stripped.find((p) => p.series.length === 0);
      if (empty) {
        throw new Error(
          `panel "${empty.title}" needs at least one series with a PromQL expression`,
        );
      }
      await onSave({
        name: name !== dashboard.name ? name : undefined,
        panels: stripped,
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit dashboard"
      subtitle={
        <span className="muted">
          PromQL placeholder <code>$service</code> resolves to{" "}
          <code>{dashboard.scope.service_id ? "this service" : "—"}</code>{" "}
          server-side
        </span>
      }
      size="xl"
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {err && (
            <span className="flash" style={{ flex: 1, fontSize: 12 }}>
              {err}
            </span>
          )}
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!dirty || saving}
            onClick={save}
          >
            <Save />
            save changes
          </button>
        </div>
      }
    >
      <div className="form-grid" style={{ marginBottom: 12 }}>
        <label>name</label>
        <div className="field">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="dashboard name"
          />
        </div>
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        {panels.map((panel, idx) => (
          <PanelEditor
            key={panel.id}
            panel={panel}
            onChange={(next) =>
              setPanels((arr) => arr.map((p, i) => (i === idx ? next : p)))
            }
            onRemove={() =>
              setPanels((arr) => arr.filter((_, i) => i !== idx))
            }
          />
        ))}
      </div>

      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() =>
            setPanels((arr) => [...arr, blankPanel(arr.length)])
          }
        >
          <Plus />
          add panel
        </button>
      </div>
    </Modal>
  );
}

interface PanelEditorProps {
  panel: Panel;
  onChange: (next: Panel) => void;
  onRemove: () => void;
}

function PanelEditor({ panel, onChange, onRemove }: PanelEditorProps) {
  const setSeries = (next: PanelSeries[]) => onChange({ ...panel, series: next });

  return (
    <div
      style={{
        border: "1px solid var(--edge)",
        borderRadius: 8,
        padding: 12,
        background: "var(--surface-2)",
        display: "grid",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          value={panel.title}
          onChange={(e) => onChange({ ...panel, title: e.target.value })}
          placeholder="panel title"
          style={{ flex: 1, minWidth: 160, fontSize: 13 }}
        />
        <label style={{ fontSize: 11 }}>
          unit{" "}
          <select
            value={panel.unit}
            onChange={(e) =>
              onChange({ ...panel, unit: e.target.value as PanelUnit })
            }
          >
            {PANEL_UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 11 }}>
          width{" "}
          <select
            value={panel.w}
            onChange={(e) => onChange({ ...panel, w: Number(e.target.value) })}
          >
            {[4, 6, 8, 12].map((w) => (
              <option key={w} value={w}>
                {w}/12
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 11 }}>
          height{" "}
          <input
            type="number"
            min={120}
            max={600}
            step={20}
            value={panel.h}
            onChange={(e) => onChange({ ...panel, h: Number(e.target.value) })}
            style={{ width: 70 }}
          />
        </label>
        <button
          type="button"
          className="btn btn--danger btn--sm"
          onClick={onRemove}
          title="remove panel"
        >
          <Trash2 />
        </button>
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: 140 }}>Legend</th>
              <th>PromQL</th>
              <th style={{ width: 70 }}>Color</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {panel.series.map((s, idx) => (
              <tr key={idx}>
                <td>
                  <input
                    value={s.legend}
                    onChange={(e) =>
                      setSeries(
                        panel.series.map((row, i) =>
                          i === idx ? { ...row, legend: e.target.value } : row,
                        ),
                      )
                    }
                    style={{ width: "100%", fontSize: 12 }}
                    placeholder="p95"
                  />
                </td>
                <td>
                  <input
                    value={s.promql}
                    onChange={(e) =>
                      setSeries(
                        panel.series.map((row, i) =>
                          i === idx ? { ...row, promql: e.target.value } : row,
                        ),
                      )
                    }
                    className="mono"
                    style={{ width: "100%", fontSize: 12 }}
                    placeholder='sum(rate(metric{service=~"$service"}[1m]))'
                    spellCheck={false}
                    autoComplete="off"
                  />
                </td>
                <td>
                  <input
                    type="color"
                    value={s.color || SERIES_COLORS[idx % SERIES_COLORS.length]}
                    onChange={(e) =>
                      setSeries(
                        panel.series.map((row, i) =>
                          i === idx ? { ...row, color: e.target.value } : row,
                        ),
                      )
                    }
                    style={{ width: 36, height: 28, padding: 0 }}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() =>
                      setSeries(panel.series.filter((_, i) => i !== idx))
                    }
                    disabled={panel.series.length === 1}
                    title="remove series"
                  >
                    <Trash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() =>
            setSeries([
              ...panel.series,
              { legend: "", promql: "", color: undefined },
            ])
          }
        >
          <Plus />
          add series
        </button>
      </div>
    </div>
  );
}

// ---- helpers ------------------------------------------------------------

function blankPanel(idx: number): Panel {
  return {
    id: `panel-${Date.now()}-${idx}`,
    title: "New panel",
    kind: "timeseries",
    unit: "short",
    w: 6,
    h: 240,
    stacked: false,
    series: [{ legend: "series", promql: "" }],
  };
}

/**
 * Pivot ``[ {id:"0", labels:{...}, points:[...]}, ... ]`` into a row
 * shape recharts can consume: ``[ {ts, s0, s1, ...}, ... ]``.
 *
 * Each request series id is the index of the originating PanelSeries.
 * Multiple result series with the same id (label-split PromQL) are
 * merged by averaging the value at each ts — keeps the chart readable
 * by default, and the operator can always rewrite the expression with
 * an explicit ``sum``/``avg`` aggregator if they want a different
 * reduction.
 */
function toRechartsRows(
  series: PromQLSeries[],
  panelSeries: PanelSeries[],
): Array<Record<string, number>> {
  if (series.length === 0) return [];
  const byTs = new Map<number, Record<string, { sum: number; count: number }>>();
  for (const s of series) {
    const key = `s${s.id}`;
    for (const p of s.points) {
      if (p.value == null) continue;
      let row = byTs.get(p.ts_ms);
      if (!row) {
        row = {};
        byTs.set(p.ts_ms, row);
      }
      const slot = row[key] ?? { sum: 0, count: 0 };
      slot.sum += p.value;
      slot.count += 1;
      row[key] = slot;
    }
  }
  const rows = Array.from(byTs.entries())
    .sort(([a], [b]) => a - b)
    .map(([ts, agg]) => {
      const out: Record<string, number> = { ts };
      for (let i = 0; i < panelSeries.length; i++) {
        const slot = agg[`s${i}`];
        if (slot) out[`s${i}`] = slot.sum / slot.count;
      }
      return out;
    });
  return rows;
}

function formatTs(ms: number): string {
  const d = new Date(ms);
  return `${d.getHours().toString().padStart(2, "0")}:${d
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
}

function formatValue(v: number, unit: PanelUnit): string {
  if (!Number.isFinite(v)) return "—";
  switch (unit) {
    case "ms":
      return `${v.toFixed(v < 10 ? 1 : 0)}ms`;
    case "reqps":
      return `${v.toFixed(v < 10 ? 2 : 0)}/s`;
    case "percent":
      return `${v.toFixed(v < 1 ? 2 : 1)}%`;
    case "usd":
      return `$${v.toFixed(2)}`;
    case "bytes":
      return formatBytes(v);
    case "short":
    default:
      return v < 10 ? v.toFixed(2) : v.toFixed(0);
  }
}

function formatBytes(v: number): string {
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let n = v;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}
